import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { ToastContainer } from "react-toastify";
import { X } from "lucide-react";
import LinkPreviewBaseLayout from "../LinkPreview/LinkPreviewBaseLayout";
import LinkPreviewModernLayout from "../LinkPreview/LinkPreviewModernLayout";
import LinkPreviewAdvancedLayout from "../LinkPreview/LinkPreviewAdvancedLayout"
import URLActionBar from "../components/URLActionBar";
import { CustomizeStep } from "./components/CustomizeStep";
import { DetailsStep } from "./components/DetailsStep";
import { ReviewStep, isPreSaveReleased } from "./components/ReviewStep";
import { StepIndicator } from "./components/StepIndicator";
import SpotifyIcon from '../../../../assets/images/admin/Spotify.svg'
import DeezerIcon from "../../../../assets/images/icons/deezer_logo.png";
import SoundCloudIcon from "../../../../assets/images/icons/soundcloud-icon.svg.svg";
import AppleMusicIcon from '../../../../assets/images/icons/apple-music.png';
import AudiomackIcon from "../../../../assets/images/icons/audio-mack.png";
import TidalIcon from "../../../../assets/images/icons/tidal.png"
import AmazonMusicIcon from "../../../../assets/images/icons/amazon-music.png"
import { showToast } from "../../../../helper/ShowToast";
import LinkAnalytics from "../components/LinkAnalytics";
import { socialPlatformsArray } from "../constants/socialPlatforms";
import { CustomizeStepSkeleton, RightPreviewSkeleton } from "../LinkSkeletons";
import {
    buildLinkMap, buildPreSaveFollowPayload, getUserTimezone, validateScanLinkFrontend,
    normalizeScanLinkForUI
} from "../LinkHelper";

import { useSmartLinkDetails, useCreatePreSaveSmartLink, useUpdatePreSaveSmartLink } from "../../../../queries/artistManger/smartLink.query";
import AppTour from "../../../../components/AppTour/AppTour";

const tourSteps = [
    {
        target: "[data-tour='profile-form']",
        content: "Fill this form to build and customize your pre-save page and later you can edit or customize your page.",
        placement: "top",
        title: "Pre-Save Page Form"
    },
    {
        target: "[data-tour='goto-step-2']",
        content: "Click on Form Next button to go to Step-2 form",
        placement: "top",
        title: "Next Button",
        // hideButtons: "next"
    },
    {
        target: "[data-tour='collect-fan-data']",
        content: "Enable these power ups to boost your followers and collect fan data with your pre-save link. You can enable or disable these features as you want.",
        placement: "top",
        title: "Power ups & Collect Fan Data"
    },
    // {
    //     target: "[data-tour='social-footer']",
    //     content: "Enable this social footer section to provide your fans option to follow you across social platforms",
    //     placement: "top",
    //     title: "Social Footer"
    // },
    // {
    //     target: "[data-tour='publish']",
    //     content: "Once you are done with building your tour link, click here to publish it and share it with your fans. You can always come back and edit or update your link whenever you want.",
    //     placement: "bottom",
    //     title: "Publish Your tour Link"
    // },
    // {
    //     target: "[data-tour='mobile-demo']",
    //     content: "See the real-time preview of your tour link on mobile screen as you build it.",
    //     placement: "left",
    //     title: "Real-time Mobile Preview"
    // },
    // {
    //     target: "[data-tour='reorder']",
    //     content: "Arrange the order of your platforms as you want them to appear on your tour link. Just drag and drop to rearrange.",
    //     placement: "left",
    //     title: "Reorder Platforms"
    // },
];

const PRESAVE_LINK_DOMAIN = "https://upnc.me";

const slugifyShortId = (text) =>
    String(text || "")
        .toLowerCase()
        .replace(/[\s_]+/g, "-")
        .replace(/[^\w-]+/g, "")
        .replace(/-+/g, "-")
        .replace(/^-+|-+$/g, "");

const platforms = [
    {
        id: "spotify",
        label: "Pre-Save on Spotify",
        text: "Fans who presave will auto-follow your profile.",
        icon: SpotifyIcon,
        background: "bg-green-600",
        enabled: true,
        order: 1,
    },
    {
        id: "deezer",
        label: "Pre-Save on Deezer",
        text: "Fans who presave will auto-follow your profile.",
        icon: DeezerIcon,
        background: "",
        enabled: true,
        order: 2,
    },
    {
        id: "apple",
        label: "Pre-Save on Apple Music",
        text: "Add platform doesn't enable auto-following.",
        icon: AppleMusicIcon,
        background: "",
        enabled: true,
        order: 3,
    },
    {
        id: "audiomack",
        label: "Pre-Save on Audiomack",
        text: "Connect Pre-save on Audiomack to enable auto-follow.",
        icon: AudiomackIcon,
        background: "bg-gray-900",
        enabled: true,
        order: 4,
    },
    {
        id: "soundcloud",
        label: "Pre-Save on SoundCloud",
        text: "Fans who presave will auto-follow your profile.",
        icon: SoundCloudIcon,
        background: "",
        enabled: true,
        order: 5,
    },
    {
        id: "amazon",
        label: "Pre-Save on Amazon Music",
        text: "Fans who presave will auto-follow your profile.",
        icon: AmazonMusicIcon,
        background: "bg-[#FFF5E6]",
        enabled: true,
        order: 6,
    },
    {
        id: "tidal",
        label: "Pre-Save on Tidal",
        text: "Fans who presave will auto-follow your profile.",
        icon: TidalIcon,
        background: "bg-[#CFF4F4]",
        enabled: true,
        order: 7,
    },
    // {
    //     id: "youtubemusic",
    //     label: "Pre-Order on Youtube",
    //     text: "Fans who presave will auto-follow your profile.",
    //     icon: YoutubeIcon,
    //     background: "",
    //     enabled: true,
    //     order: 8,
    // },
];


export default function PreSaveLink() {
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();
    const isEditMode = Boolean(id);
    const [shortIdEdited, setShortIdEdited] = useState(false);
    const [runTour, setRunTour] = useState(false)
    const [tourTarget, setTourTarget] = useState(null)
    const [currentStep, setCurrentStep] = useState(1);
    const [activeTab, setActiveTab] = useState("Build");
    const tabs = ["Build", "Analytics"];
    const artistDetails = useSelector(state => state.artistDetails);
    const { artistInfo } = artistDetails;
    const [socialPlatforms, setSocialPlatforms] = useState(socialPlatformsArray);
    const [preSaveLinkData, setPreSaveLinkData] = useState({
        releaseTitle: '',
        shortId: '',
        description: '',
        preReleaseDescription: '',
        image: '',
        artistName: '',
        imageSource: "artist",   // "artist" | "uploaded"
        imageUrl: "",            // final image URL used
        imageFile: null,         // File if uploaded
        imageFileName: "",
        imageChanged: false,
        pageLayout: 'modern_layout',
        selectedBackground: 'color',
        selectedColor: "#5CBDC7",
        background: 'color',
        bgImagePreview: "",
        bgImageFile: null,
        bgImageFileName: "",
        bgImageChanged: false,
        afterPresaveRedirectUrl: '',
        // step 2
        emailCollection: {
            enabled: true,
            actionName: "Subscribe"
        },
        preSaveFollow: true,
        enableFutureSave: true,
        scanLink: '', // target link/upc/isrc
        scanLinkType: 'url',
        releaseDate: null,
        releaseTime: "00:00",
        timezone: getUserTimezone(),
        collectFanData: true,
        boostFollowers: true,
        futureSaves: true,
        saveSuccessfulMessage: '',
        // -----NOTIFICATIONS ----------
        notificationEnabled: true,
        notificationSubject: "",
        notificationMessage: "",
        notificationSenderEmail: "no-reply@feature.fm",
        notificationFooter: "",
        //--------
    });
    const [platformsState, setPlatformsState] = useState(platforms);

    const {
        data: preSaveDetails,
        isLoading: isLinkDetailsLoading
    } = useSmartLinkDetails(id, isEditMode);
    const {
        mutate: createPreSave,
        isPending: isCreating,
    } = useCreatePreSaveSmartLink({ navigate });

    const {
        mutate: updatePreSave,
        isPending: isUpdating,
    } = useUpdatePreSaveSmartLink({ setCurrentStep });

    const isLoading = isCreating || isUpdating;

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get("tab") === "analytics") {
            setActiveTab("Analytics");
        }
    }, [location.search]);

    useEffect(() => {
        if (
            artistInfo?.avatar &&
            preSaveLinkData.imageSource === "artist"
        ) {
            setPreSaveLinkData(prev => ({
                ...prev,
                imageUrl: artistInfo.avatar,
                artistName: artistInfo.name,

            }));
        }
        setPreSaveLinkData(prev => ({
            ...prev,
            artistName: artistInfo.name,
        }));
        if (artistInfo?.links) {
            const linkMap = buildLinkMap(artistInfo.links);
            // Update social
            setSocialPlatforms(prev =>
                prev.map(platform => {
                    const match = linkMap[platform.name.toLowerCase()];
                    return match ? { ...platform, url: match } : platform;
                })
            );
        }

    }, [artistInfo, preSaveLinkData.imageSource]);

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    }, [currentStep]);

    useEffect(() => {
        return () => {
            if (preSaveLinkData.bgImagePreview) {
                URL.revokeObjectURL(preSaveLinkData.bgImagePreview);
            }
        };
    }, [preSaveLinkData.bgImagePreview]);

    const detectScanLinkType = (scanLink = "") => {
        if (!scanLink) return "url";

        if (/^UPC:/i.test(scanLink)) return "upc";
        if (/^ISRC:/i.test(scanLink)) return "isrc";

        return "url"; // default URL
    };

    const mergePlatforms = (basePlatforms, enabledStores = []) => {
        const enabledIds = enabledStores.map(s => s.storeId);

        return basePlatforms
            .map(p => ({
                ...p,
                enabled: enabledIds.includes(p.id),
            }))
            .sort((a, b) => {
                const aIndex = enabledIds.indexOf(a.id);
                const bIndex = enabledIds.indexOf(b.id);

                if (aIndex === -1 && bIndex === -1) return 0;
                if (aIndex === -1) return 1;
                if (bIndex === -1) return -1;

                return aIndex - bIndex;
            });
    };

    const getAfterPresaveRedirectUrl = (stores = []) => {
        if (!Array.isArray(stores)) return "";

        // Prefer Spotify fallbackUrl
        const spotifyStore = stores.find(
            s => s.storeId === "spotify" && s.fallbackUrl
        );
        if (spotifyStore) return spotifyStore.fallbackUrl;

        // Otherwise, take first available fallbackUrl
        const anyFallback = stores.find(s => s.fallbackUrl);
        return anyFallback?.fallbackUrl || "";
    };

    useEffect(() => {
        if(location.hash === "#start-tour"){
            setRunTour(true)
        }
    }, [location.hash])

    useEffect(() => {
        if (isEditMode || shortIdEdited) return;
        const title = preSaveLinkData.releaseTitle?.trim();
        if (!title) return;

        const artistName = artistInfo?.name?.trim() || "artist";
        const nextShortId = slugifyShortId(`${artistName}-${title}`);
        setPreSaveLinkData((prev) => (
            prev.shortId === nextShortId ? prev : { ...prev, shortId: nextShortId }
        ));
    }, [preSaveLinkData.releaseTitle, artistInfo?.name, isEditMode, shortIdEdited]);

    // useEffect(() => {
    //     if(tourTarget === "[data-tour='collect-fan-data']"){
    //         setCurrentStep(2)
    //     }
    // }, [tourTarget])

    useEffect(() => {
        if (!preSaveDetails || Object.keys(preSaveDetails).length === 0) return;

        const redirectUrl = getAfterPresaveRedirectUrl(
            preSaveDetails?.customization?.stores
        );
        if (isEditMode && preSaveDetails) {
            setPreSaveLinkData({
                releaseTitle: preSaveDetails.title,
                description: preSaveDetails.description,
                preReleaseDescription: preSaveDetails.preReleaseDescription,

                imageSource: 'presave',
                imageUrl: preSaveDetails.image,
                imageFile: null,
                imageFileName: "",
                imageChanged: false,

                pageLayout: preSaveDetails.customization.pageLayout,
                selectedBackground: preSaveDetails.customization.backgroundType,
                selectedColor: preSaveDetails.customization.backgroundColor,
                bgImagePreview: preSaveDetails.customization.backgroundImageUrl || "",
                bgImageFile: null,
                bgImageFileName: "",
                bgImageChanged: false,
                afterPresaveRedirectUrl: redirectUrl,
                scanLink: normalizeScanLinkForUI(preSaveDetails.featureFmMeta?.scanLink) || "",
                scanLinkType: detectScanLinkType(preSaveDetails.featureFmMeta?.scanLink),

                releaseDate: preSaveDetails.release?.date,
                releaseTime: preSaveDetails.release?.time,
                isConverted: Boolean(preSaveDetails.release?.isConverted),
                timezone: preSaveDetails.release?.timezone,
                preSaveFollow:
                    Array.isArray(preSaveDetails.preSaveFollow) &&
                    preSaveDetails.preSaveFollow.length > 0,
                emailCollection: preSaveDetails?.customization?.emailCollection,
                enableFutureSave: preSaveDetails.enableFutureSave,

                saveSuccessfulMessage: preSaveDetails.customization.saveSuccessfulMessage,

                notificationEnabled: preSaveDetails.notifications?.toggle,
                notificationSubject: preSaveDetails.notifications?.subject,
                notificationMessage: preSaveDetails.notifications?.message,
                notificationFooter: preSaveDetails.notifications?.footer,
            });

            setPlatformsState(
                mergePlatforms(platforms, preSaveDetails?.customization?.enabledStores)
            );
        }
    }, [preSaveDetails, isEditMode]);

    const isValidUrl = (value) => {
        try {
            const url = new URL(value);
            return url.protocol === "http:" || url.protocol === "https:";
        } catch (err) {
            return false;
        }
    };

    const validateStepOne = () => {
        const {
            releaseTitle,
            description,
            preReleaseDescription,
            selectedBackground,
            bgImagePreview,
            bgImageFile,
            bgImageFileName,
            afterPresaveRedirectUrl,
            imageSource,
            imageChanged,
            imageFile
        } = preSaveLinkData;

        if (!releaseTitle?.trim()) {
            showToast('warning', "Release title is required")
            return false;
        }

        if (!preReleaseDescription?.trim()) {
            showToast('warning', "Pre-Save Subtitle is required")
            return false;
        }

        if (!description?.trim()) {
            showToast('warning', "Post-Release Subtitle is required")
            return false;
        }

        if (imageSource === "uploaded" && imageChanged && !imageFile) {
            showToast("Artwork image is required");
            return false;
        }

        if (selectedBackground === "image") {
            // Existing image (edit mode)
            if (!preSaveLinkData.bgImageChanged) {
                if (!bgImagePreview) {
                    showToast("Background image is required");
                    return false;
                }
            }

            // New image selected
            if (preSaveLinkData.bgImageChanged) {
                if (!bgImageFile || !bgImageFileName) {
                    showToast("Background image is required");
                    return false;
                }
            }
        }
        if (!afterPresaveRedirectUrl?.trim()) {
            showToast('warning', "After Presave Redirect Url Url is required")
            return false;
        }
        if (!isValidUrl(afterPresaveRedirectUrl?.trim())) {
            showToast(
                "warning",
                "Please enter a valid URL"
            );
            return false;
        }
        return true;
    };

    const validateStepTwo = () => {
        const {
            scanLink,
            scanLinkType,
            releaseDate,
            releaseTime,
            notificationEnabled,
            notificationSubject,
            notificationMessage,
            saveSuccessfulMessage,
        } = preSaveLinkData;

        // Scan link (required)
        if (!scanLink?.trim()) {
            showToast("warning", "Scan link is required");
            return false;
        }

        // Scan link type (required)
        if (!scanLinkType) {
            showToast("warning", "Scan link type is required");
            return false;
        }

        // Only validate URL if scanLinkType is "link"
        if (scanLinkType === "link") {
            if (!isValidUrl(scanLink?.trim())) {
                showToast("warning", "Scan link must be a valid URL");
                return false;
            }
        }

        // Release date & time
        if (!releaseDate) {
            showToast("warning", "Release date is required");
            return false;
        }

        if (!releaseTime) {
            showToast("warning", "Release time is required");
            return false;
        }

        // Notifications (conditional)
        if (notificationEnabled) {
            if (!notificationSubject?.trim()) {
                showToast("warning", "Notification subject is required");
                return false;
            }

            if (!notificationMessage?.trim()) {
                showToast("warning", "Notification message is required");
                return false;
            }
        }

        // Save success message
        if (!saveSuccessfulMessage?.trim()) {
            showToast("warning", "Save successful message is required");
            return false;
        }

        return true;
    };

    const userHasAnyUrl = socialPlatforms.some(p => p.url && p.url.trim() !== "");
    const activePlatforms = userHasAnyUrl
        ? socialPlatforms
        : socialPlatformsArray;

    const handleNext = () => {
        // Step 1 validation
        if (currentStep === 1) {
            if (!validateStepOne()) return;
        }
        // Step 2 validation
        if (currentStep === 2) {
            if (!validateStepTwo()) return;
        }
        setCurrentStep(prev => Math.min(3, prev + 1));
    }

    const goNextStep = () => {
        setCurrentStep(prev => Math.min(3, prev + 1));
    }

    const goBack = () => navigate('/admin/artist-manager/link-and-fan-hub');

    const steps = [
        { number: 1, name: 'Customize', status: currentStep > 1 ? 'complete' : currentStep === 1 ? 'current' : 'upcoming' },
        { number: 2, name: 'Details', status: currentStep > 2 ? 'complete' : currentStep === 2 ? 'current' : 'upcoming' },
        { number: 3, name: 'Review', status: currentStep === 3 ? 'current' : 'upcoming' }
    ];


    const enabledPlatforms = platformsState
        .filter(p => p.enabled)
        .map(p => {
            const isSpotify = p.id === "spotify";
            return {
                storeId: p.id,
                url: "",
                ...(isSpotify &&
                    preSaveLinkData.afterPresaveRedirectUrl && {
                    fallbackUrl: preSaveLinkData.afterPresaveRedirectUrl || "",
                }),
            };
        });

    const buildScanLinkPayload = (data) => {
        const { scanLinkType, scanLink } = data;

        if (!scanLink) return "";

        switch (scanLinkType) {
            case "upc":
                return `UPC:${scanLink}`;

            case "isrc": {
                const cleaned = scanLink
                    .replace(/^ISRC:/i, "")
                    .replace(/-/g, "")
                    .toUpperCase();
                return `ISRC:${cleaned}`;
            }

            case "url":
            default:
                return scanLink;
        }
    };

    const buildPreSaveFormData = () => {
        const { valid, message } = validateScanLinkFrontend(
            preSaveLinkData.scanLink,
            preSaveLinkData.scanLinkType
        );

        if (!valid) {
            showToast("warning", message);
            return null;
        }

        const formData = new FormData();

        // ---------- BASIC ----------
        formData.append("title", preSaveLinkData.releaseTitle);
        if (!isEditMode && preSaveLinkData.shortId?.trim()) {
            formData.append("shortId", preSaveLinkData.shortId.trim().toLowerCase());
        }
        formData.append("description", preSaveLinkData.description);
        formData.append("preReleaseDescription", preSaveLinkData.preReleaseDescription);
        formData.append("imageSource", preSaveLinkData.imageSource);
        formData.append("imageUrl", preSaveLinkData.imageUrl || "");

        // ---------- SCAN LINK ----------
        formData.append(
            "scanLink",
            buildScanLinkPayload({
                scanLinkType: preSaveLinkData.scanLinkType,
                scanLink: preSaveLinkData.scanLink
            })
        );
        formData.append("scanLinkType", preSaveLinkData.scanLinkType);

        // ---------- RELEASE ----------
        formData.append("releaseDate", preSaveLinkData.releaseDate);
        formData.append("releaseTime", preSaveLinkData.releaseTime);
        formData.append("timezone", preSaveLinkData.timezone);

        // ---------- IMAGES ----------
        if (
            preSaveLinkData.imageSource === "uploaded" &&
            preSaveLinkData.imageFile
        ) {
            formData.append("image", preSaveLinkData.imageFile);
        }

        if (
            preSaveLinkData.selectedBackground === "image" &&
            preSaveLinkData.bgImageChanged &&
            preSaveLinkData.bgImageFile
        ) {
            formData.append("backgroundImage", preSaveLinkData.bgImageFile);
        }

        // ---------- CUSTOMIZATION ----------
        formData.append(
            "customization",
            JSON.stringify({
                pageLayout: preSaveLinkData.pageLayout,
                backgroundType: preSaveLinkData.selectedBackground,
                backgroundColor: preSaveLinkData.selectedColor,
                saveSuccessfulMessage: preSaveLinkData.saveSuccessfulMessage,
                stores: enabledPlatforms,
                enabledStores: enabledPlatforms.map(s => ({
                    storeId: s.storeId,
                    actionName: "Pre-Save"
                })),
                socialIcons: {
                    enabled: true,
                    socialLinks: socialPlatforms.reduce((acc, p) => {
                        if (p.url) acc[p.name.toLowerCase()] = p.url;
                        return acc;
                    }, {})
                }
            })
        );

        // ---------- EMAIL COLLECTION ----------
        formData.append(
            "emailCollection",
            JSON.stringify(preSaveLinkData.emailCollection)
        );

        // ---------- PRE SAVE FOLLOW ----------
        if (preSaveLinkData.preSaveFollow) {
            const preSaveFollowPayload = buildPreSaveFollowPayload(artistInfo);
            if (preSaveFollowPayload.length > 0) {
                formData.append(
                    "preSaveFollow",
                    JSON.stringify(preSaveFollowPayload)
                );
            }
        }

        formData.append("enableFutureSave", preSaveLinkData.enableFutureSave);

        // ---------- NOTIFICATIONS ----------
        const notificationPayload = {
            toggle: preSaveLinkData.notificationEnabled,
            subject: preSaveLinkData.notificationSubject,
            message: preSaveLinkData.notificationMessage,
            releaseName: preSaveLinkData.releaseTitle,
            hideFFMBranding: true,
            sender: preSaveLinkData.notificationSender,
            footer: preSaveLinkData.notificationFooter
        };

        if (artistInfo?.name?.trim()) {
            notificationPayload.artistName = artistInfo.name?.trim();
        }
        if (artistInfo?.avatar) {
            notificationPayload.artistImage = artistInfo.avatar;
        }

        formData.append(
            "notifications",
            JSON.stringify(notificationPayload)
        );

        return formData;
    };


    const handlePublish = () => {
        const formData = buildPreSaveFormData();
        if (!formData) return;
        createPreSave(formData);
    };

    const handleUpdate = () => {
        if (!validateStepOne() || !validateStepTwo()) return;
        const formData = buildPreSaveFormData();
        if (!formData) return;
        updatePreSave({ id, formData });
    };

    const previewData = {
        background: {
            type: preSaveLinkData?.selectedBackground,
            color: preSaveLinkData?.selectedColor,
            image: preSaveLinkData?.bgImagePreview,
            fallback: "#5CBDC7"
        },

        release: {
            image: preSaveLinkData?.imageUrl || preSaveLinkData?.imageFile ||
                require("../../../../assets/images/karan.png"),
            name: preSaveLinkData?.releaseTitle || "Release title",
            subtitle: (isPreSaveReleased(preSaveLinkData)
                ? preSaveLinkData?.description
                : preSaveLinkData?.preReleaseDescription)?.trim()
                || (isPreSaveReleased(preSaveLinkData) ? "Post-Release Subtitle" : "Pre-Save Subtitle"),
            artist: artistInfo.name?.trim() || "Artist name"
        },

        player: {
            isPlaying: false,
            onToggle: false
        },

        socialPlatforms: activePlatforms
            .filter(p => (userHasAnyUrl ? p.url : true))
            .map(p => ({
                id: p.id,
                name: p.name,
                url: p.url || "#",
                icon: p.icon
            })),

        streamingPlatforms: platformsState,
        mode : "presave"
    };

    const handleTourCallback = (data) => {
        if(data.step.target === "[data-tour='collect-fan-data']"){
            setRunTour(true)
            setCurrentStep(2)
        }
    }
   
    return (
        <>
            <div className="BuildBioLink-Page font-jakarta">
                <ToastContainer />
                <AppTour
                    tourSteps={tourSteps}
                    runTour={runTour}
                    setRunTour={setRunTour}
                    setTarget={setTourTarget}
                    callback={handleTourCallback}
                />
                {/* Header */}
                <div className="sticky top-0 shadow z-30 bg-theme_clr1 text-white px-4 py-3">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                            <button onClick={goBack} className="shrink-0">
                                <X className="w-5 h-5" />
                            </button>
                            <h1 className="text-sm sm:text-lg font-semibold truncate">
                                {activeTab === "Build" ? `${isEditMode ? "Edit" : "Build a"} Pre-Save Link` : "Pre-Save Link Analytics"}
                            </h1>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full sm:w-auto lg:min-w-0 lg:flex-1 lg:justify-end">
                            {((isEditMode && preSaveDetails?.fullLink) || (!isEditMode && (preSaveLinkData.shortId || shortIdEdited))) && (
                                <div className="w-full sm:w-[350px] lg:w-auto lg:min-w-[350px] lg:max-w-[640px] lg:flex-1">
                                    {isEditMode ? (
                                        <URLActionBar
                                            fullLink={preSaveDetails?.fullLink}
                                        />
                                    ) : (
                                        <URLActionBar
                                            fullLink={`${PRESAVE_LINK_DOMAIN}/${preSaveLinkData.shortId}`}
                                            linkType="presave"
                                            domain={PRESAVE_LINK_DOMAIN}
                                            shortId={preSaveLinkData.shortId}
                                            isPreview
                                            onChangeShortId={(newShortId) => {
                                                setShortIdEdited(true);
                                                setPreSaveLinkData((prev) => ({
                                                    ...prev,
                                                    shortId: newShortId,
                                                }));
                                            }}
                                        />
                                    )}
                                </div>
                            )}
                            {activeTab === "Build" && (
                                <button
                                    className={`bg-theme_clr2 px-4 sm:px-6 py-2 text-white text-sm sm:text-base font-semibold rounded-full flex items-center justify-center whitespace-nowrap
                                    ${(isLoading || (!isEditMode && currentStep !== 3)) ? "opacity-60 cursor-not-allowed" : ""}`}
                                    onClick={isEditMode ? handleUpdate : handlePublish}
                                    disabled={isLoading || (!isEditMode && currentStep !== 3)}
                                >
                                    {isLoading ? (
                                        <div className="flex items-center gap-2">
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            {isEditMode ? "Saving..." : "Publishing..."}
                                        </div>
                                    ) : (
                                        isEditMode ? "Save Changes" : "Publish"
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
                {/* </div> */}

                <div className="container">
                    {/* Tabs Menu */}
                    <div className={`lg:w-[calc(100%-405px)] shadow relative z-20 flex border-2 border-theme_clr1 bg-white rounded-b-xl mb-4 px-4`}>
                        {tabs.map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                disabled={tab === 'Analytics' && !isEditMode}
                                className={`font-semibold border-b-2 border-theme_clr1 mb-[-2px] px-4 py-2 ${activeTab === tab
                                    ? "border-theme_clr2 text-theme_clr2"
                                    : "text-theme_clr1"
                                    } ${(tab === 'Analytics' && !isEditMode) ? 'opacity-50' : ''}`}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>

                    <div className="flex flex-col-reverse lg:flex-row lg:justify-between gap-6">
                        {/* LEFT SECTION */}
                        <div className="left-section flex-1 lg:w-[calc(100%-374px)]">
                            {/* Tab Content */}
                            {activeTab === "Build" && (
                                <>
                                    <div className="BuildBioLink-Page font-jakarta">
                                        <div className="bg-white border-b">
                                            <div className="max-w-7xl mx-auto px-6 py-6">
                                                <div className="flex items-center justify-center gap-8">
                                                    {steps.map((step, index) => (
                                                        <React.Fragment key={step.number}>
                                                            <StepIndicator step={step} />
                                                            {index < steps.length - 1 && (
                                                                <div className={`flex-1 h-0.5 ${step.status === 'complete' ? 'bg-green-500' : 'bg-gray-300'}`} style={{ maxWidth: '100px' }}></div>
                                                            )}
                                                        </React.Fragment>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="max-w-7xl mx-auto px-2 py-8">
                                            <div className="grid grid-cols-1 lg:grid-cols-1 gap-8">
                                                <div className="lg:col-span-2">
                                                    <div className="bg-white rounded-lg border-2 border-blue-900 p-6" data-tour="profile-form">
                                                        {currentStep === 1 && (
                                                            isLinkDetailsLoading && isEditMode ? (
                                                                <CustomizeStepSkeleton />
                                                            ) : (
                                                                <CustomizeStep
                                                                    preSaveLinkData={preSaveLinkData}
                                                                    setPreSaveLinkData={setPreSaveLinkData}
                                                                    artistInfo={artistInfo}
                                                                />
                                                            )
                                                        )}
                                                        {currentStep === 2 && <DetailsStep preSaveLinkData={preSaveLinkData} setPreSaveLinkData={setPreSaveLinkData} platformsState={platformsState} setPlatformsState={setPlatformsState} />}
                                                        {currentStep === 3 && <ReviewStep preSaveLinkData={preSaveLinkData} setPreSaveLinkData={setPreSaveLinkData} platformsState={platformsState} />}
                                                    </div>
                                                </div>
                                            </div>

                                            <hr className="border-t border-gray-400 shadow my-8" />

                                            <div className="flex justify-evenly items-center mt-8">
                                                <button
                                                    onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
                                                    disabled={currentStep === 1}
                                                    className="px-4 py-1 border-2 border-gray-300 text-theme_clr1 rounded-full font-semibold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                                                >
                                                    {currentStep === 1 ? 'Back' : 'Prev'}
                                                </button>
                                                <button
                                                    data-tour="goto-step-2"
                                                    onClick={() => {
                                                        if (currentStep === 3) {
                                                            isEditMode ? handleUpdate() : handlePublish()
                                                        } else {
                                                            location.hash === "#start-tour" ? goNextStep() : handleNext();
                                                        }
                                                    }}
                                                    disabled={isLoading}
                                                    className={`
                                                                 px-4 py-1 rounded-full font-semibold transition
                                                                 ${currentStep === 3
                                                            ? "bg-green-600 hover:bg-green-700"
                                                            : "bg-theme_clr2 hover:bg-orange-600"}
                                                                           text-white
                                                                           disabled:opacity-50
                                                                            disabled:cursor-not-allowed
                                                                `}
                                                >
                                                    {isLoading
                                                        ? isEditMode ? "Saving..." : "Publishing..."
                                                        : currentStep === 3
                                                            ? isEditMode ? "Save Changes" : "Publish"
                                                            : "Next"}
                                                </button>

                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            {activeTab === "Analytics" && (
                                <div className="bg-white border-2 border-theme_clr1 rounded-xl p-4 xl:p-6 mb-5">
                                    <LinkAnalytics smartLinkId={id} />
                                </div>
                            )}
                        </div>

                        {/* RIGHT SECTION (unchanged) */}
                        {isLinkDetailsLoading && isEditMode ? (
                            <RightPreviewSkeleton />
                        ) : (
                            <div className="right-section w-[350px] mx-auto mt-6">
                                {
                                    preSaveLinkData?.pageLayout === "base_layout" ?
                                        <LinkPreviewBaseLayout previewData={previewData} /> :
                                        preSaveLinkData?.pageLayout === "action_page" ?
                                            <LinkPreviewAdvancedLayout previewData={previewData} /> :
                                            <LinkPreviewModernLayout previewData={previewData} />
                                }
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

